import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TextSearch from '../../controls/TextSearch';
import { styled } from '@mui/material/styles';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import * as accountingSlice from '../../../data/accounting.slice';
import {
  ProviderInvoiceLineItem,
  NeedsReviewResponse,
  ProviderGroup,
} from '../../../data/models/provider-invoice-line-item.model';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  TablePagination,
  useTheme,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
} from '@mui/material';
import { GridCellParams, GridColDef, DataGridPro } from '@mui/x-data-grid-pro';
import { CellDualValue } from '../../tables/DataGridPro/CellDualValue';
import { CellActions } from '../../tables/DataGridPro/CellActions';
import { ButtonException } from '../../tables/DataGridPro/ButtonException';
import ReviewInvoiceDialog from './ReviewInvoiceDialog';
import { TripLegStatusBC } from '../../../data/models/trip-leg-status.model';
import * as userSlice from '../../../data/user.slice';

const PREFIX = 'Invoices';

const classes = {
  container: `${PREFIX}-container`,
  cardMain: `${PREFIX}-cardMain`,
  cardHeader: `${PREFIX}-cardHeader`,
  toolbar: `${PREFIX}-toolbar`,
  toolContainer: `${PREFIX}-toolContainer`,
  toolInput: `${PREFIX}-toolInput`,
  grid: `${PREFIX}-grid`,
  tierBadge: `${PREFIX}-tierBadge`,
  groupByContainer: `${PREFIX}-groupByContainer`,
  groupByLabel: `${PREFIX}-groupByLabel`,
  groupByValue: `${PREFIX}-groupByValue`,
  providerContainer: `${PREFIX}-providerContainer`,
  providerContent: `${PREFIX}-providerContent`,
  providerText: `${PREFIX}-providerText`,
  providerSubText: `${PREFIX}-providerSubText`,
  addressContainer: `${PREFIX}-addressContainer`,
  invoiceContainer: `${PREFIX}-invoiceContainer`,
  invoiceText: `${PREFIX}-invoiceText`,
  accordion: `${PREFIX}-accordion`,
  accordionSummary: `${PREFIX}-accordionSummary`,
  accordionDetails: `${PREFIX}-accordionDetails`,
  providerHeader: `${PREFIX}-providerHeader`,
  providerHeaderText: `${PREFIX}-providerHeaderText`,
  providerCount: `${PREFIX}-providerCount`,
  groupedGridContainer: `${PREFIX}-groupedGridContainer`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    flexGrow: 1,
    display: 'flex',
    padding: theme.spacing(3),
    gap: theme.spacing(3),
  },
  [`& .${classes.cardMain}`]: {
    width: '100%',
    padding: `${theme.spacing(3)} ${theme.spacing(4)}`,
    display: 'flex',
    flexDirection: 'column',
    gap: 13,
  },
  [`& .${classes.cardHeader}`]: {
    padding: `${theme.spacing(1)} 0`,
    '& .MuiCardHeader-title': {
      fontSize: 30,
      fontWeight: 700,
      fontFamily: 'Bitter',
      lineHeight: '40px',
      letterSpacing: '0.25px',
      color: theme.palette.lightTextPrimary.main,
    },
  },
  [`& .${classes.toolbar}`]: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '24px',
  },
  [`& .${classes.toolContainer}`]: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '24px',
    marginBottom: theme.spacing(2),
  },
  [`& .${classes.toolInput}`]: {
    width: '179px',
    height: '48px',
    borderColor: 'transparent !important',
    borderRadius: '3px',
    background: theme.palette.secondary.main,
    boxShadow: `0px 2px 7px 0px ${theme.palette.lightActionDisabled15p.main}`,
    fontFamily: 'Source Sans Pro',
    fontSize: '16px',
    fontStyle: 'normal',
    fontWeight: '600',
    lineHeight: '24px',
    letterSpacing: '0.15px',
  },
  [`& .${classes.toolInput} fieldset`]: {
    display: 'none',
  },
  [`& .${classes.toolInput} input`]: {
    color: theme.palette.lightActionDisabled60p.main,
    padding: `${theme.spacing(1.5)} 0`,

    '&::placeholder': {
      color: theme.palette.lightActionDisabled60p.main,
    },
  },
  [`& .${classes.toolInput} .MuiSelect-select`]: {
    padding: theme.spacing(1.5),
    color: theme.palette.lightActionDisabled90p.main,
    display: 'flex',
    alignItems: 'center',
  },
  [`& .${classes.grid}`]: {
    border: 'none',
    flexGrow: 'unset',
    height: 'fit-content',
    '&.empty': {
      height: 300,
    },
    '& .MuiDataGrid-columnHeaderTitle, & .MuiDataGrid-columnHeaderTitleContainerContent, & .MuiDataGrid-columnHeaderTitleContainerContent .MuiTypography-root':
      {
        width: '100%',
        color: theme.palette.lightTextPrimary.main,
        fontFamily: 'Source Sans Pro',
        fontSize: 14,
        fontStyle: 'normal',
        fontWeight: 600,
        lineHeight: 24,
        letterSpacing: 0.17,
        paddingLeft: 0,
      },
    '& .MuiDataGrid-columnSeparator': {
      display: 'none',
    },
    '& .MuiDataGrid-cell': {
      fontFamily: 'Source Sans Pro',
      fontSize: '14px',
      fontWeight: 400,
      lineHeight: '20.02px',
      letterSpacing: '0.17px',
      color: theme.palette.lightActionDisabled90p.main,
    },
    [`& .MuiDataGrid-row`]: {
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: theme.palette.lightBackgroundPaperHover.main,
      },
      '& .MuiDataGrid-cell:last-child': {
        flex: 1,
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        paddingRight: theme.spacing(2),
      },
      '&.assigned-row': {
        opacity: 0.5,
        backgroundColor: theme.palette.action.disabledBackground,
        cursor: 'not-allowed',
        '&:hover': {
          backgroundColor: theme.palette.action.disabledBackground,
        },
      },
    },
  },
  '& .MuiCardContent-root': {
    padding: 0,
  },
  [`& .${classes.groupByContainer}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  [`& .${classes.groupByLabel}`]: {
    fontSize: '12px',
    color: theme.palette.lightActionDisabled60p.main,
    fontWeight: 400,
    lineHeight: '16px',
  },
  [`& .${classes.groupByValue}`]: {
    fontWeight: 600,
    fontSize: '16px',
    color: theme.palette.lightActionDisabled90p.main,
    lineHeight: '24px',
  },
  [`& .${classes.providerContainer}`]: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
  },
  [`& .${classes.providerContent}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  [`& .${classes.providerText}`]: {
    fontFamily: 'Source Sans Pro',
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '20.02px',
    letterSpacing: '0.17px',
    color: theme.palette.lightActionDisabled90p.main,
  },
  [`& .${classes.providerSubText}`]: {
    fontFamily: 'Source Sans Pro',
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '20.02px',
    letterSpacing: '0.17px',
    color: theme.palette.lightActionDisabled60p.main,
  },
  [`& .${classes.addressContainer}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    fontFamily: 'Source Sans Pro',
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '20.02px',
    letterSpacing: '0.17px',
    color: theme.palette.lightActionDisabled90p.main,
  },
  [`& .${classes.invoiceContainer}`]: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  },
  [`& .${classes.invoiceText}`]: {
    fontFamily: 'Source Sans Pro',
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '20.02px',
    letterSpacing: '0.17px',
    color: theme.palette.lightActionDisabled90p.main,
  },
  [`& .${classes.accordion}`]: {
    backgroundColor: 'transparent',
    boxShadow: 'none',
    border: 'none',
    borderRadius: '0 !important',
    margin: 0,
    '&:before': {
      display: 'none',
    },
    '&.Mui-expanded': {
      margin: 0,
    },
  },
  [`& .${classes.accordionSummary}`]: {
    padding: `${theme.spacing(2)} ${theme.spacing(2)}`,
    minHeight: '56px',
    flexDirection: 'row-reverse',
    '&:hover': {
      backgroundColor: theme.palette.lightBackgroundPaperHover.main,
    },
    '&.Mui-expanded': {
      minHeight: '56px',
    },
    '& .MuiAccordionSummary-content': {
      margin: 0,
      marginLeft: theme.spacing(1),
      '&.Mui-expanded': {
        margin: 0,
        marginLeft: theme.spacing(1),
      },
    },
    '& .MuiAccordionSummary-expandIconWrapper': {
      marginRight: 0,
    },
  },
  [`& .${classes.accordionDetails}`]: {
    padding: 0,
    display: 'block',
    backgroundColor: theme.palette.secondary.main,
  },
  [`& .${classes.providerHeader}`]: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    width: '100%',
  },
  [`& .${classes.providerHeaderText}`]: {
    fontFamily: 'Source Sans Pro',
    fontSize: '14px',
    fontWeight: 600,
    lineHeight: '20px',
    color: theme.palette.lightTextPrimary.main,
  },
  [`& .${classes.providerCount}`]: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '20px',
    height: '20px',
    borderRadius: '100%',
    backgroundColor: theme.palette.lightActionDisabled08p.main,
    fontFamily: 'Source Sans Pro',
    fontSize: '12px',
    fontWeight: 600,
    lineHeight: '20px',
    color: theme.palette.lightTextPrimary.main,
  },
  [`& .${classes.groupedGridContainer}`]: {
    marginBottom: 0,
  },
}));

const StyledTierChip = styled(Chip)(({ theme }) => ({
  height: '20px',
  minWidth: '20px',
  '& .MuiChip-label': {
    borderRadius: '50%',
    padding: '0 ' + theme.spacing(0.75),
    fontSize: '12px',
    fontWeight: 600,
    backgroundColor: theme.palette.lightActionDisabled08p.main,
    color: theme.palette.lightTextPrimary.main + ' !important',
  },
}));

interface InvoiceData {
  id: string;
  pickup: string;
  pickupDate: string;
  tripCode: string;
  provider: string;
  driverName: string;
  driverID?: number;
  driverBadge?: number;
  puAddress: string;
  doAddress: string;
  memberName: string;
  memberId: string;
  invoice: string;
  rawData?: ProviderInvoiceLineItem;
}

const parseAddress = (address: string) => {
  const parts = address.split(',').map((part) => part.trim());
  if (parts.length >= 3) {
    const addressLine1 = parts[0];
    const cityStateZip = `${parts[1]}, ${parts[2]}`;
    return { addressLine1, cityStateZip };
  }
  return { addressLine1: address, cityStateZip: '' };
};

const transformApiDataToInvoices = (
  lineItems: ProviderInvoiceLineItem[],
): InvoiceData[] => {
  return lineItems.map((item) => {
    const { tripLeg } = item;
    const pickupDateTime = DateTime.fromISO(tripLeg.pickUpDueTimeUTC);
    const driverName = tripLeg.driver
      ? `${tripLeg.driver.driverFirstName} ${tripLeg.driver.driverLastName}`
      : '';

    return {
      id: item.providerInvoiceLineItemID.toString(),
      pickup: pickupDateTime.toFormat('MM/dd/yyyy h:mma'),
      pickupDate: tripLeg.pickUpDueTimeUTC,
      tripCode: tripLeg.tripLegReferenceID,
      provider: tripLeg.transportationProvider.transportationProviderName,
      driverName: driverName,
      puAddress: `${tripLeg.pickUpAddressLine1}, ${tripLeg.pickUpCity}, ${tripLeg.pickUpStateCode}, ${tripLeg.pickUpZipCode}`,
      doAddress: `${tripLeg.dropOffAddressLine1}, ${tripLeg.dropOffCity}, ${tripLeg.dropOffStateCode}, ${tripLeg.dropOffZipCode}`,
      memberName: `${tripLeg.reservation.passenger.passengerFirstName} ${tripLeg.reservation.passenger.passengerLastName}`,
      memberId: tripLeg.reservation.reservationReferenceID,
      invoice: item.invoiceNumber ?? `${item.providerInvoiceID}`,
      driverID: tripLeg.driver?.driverID,
      rawData: item,
    };
  });
};

export default function ReviewInvoices() {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const user = useAppSelector(userSlice.selectLoggedInUserInfo);
  const userAccountID = user?.userAccountID || 0;

  const [groupBy, setGroupBy] = useState('none');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<InvoiceData | null>(
    null,
  );
  const [expandedProviders, setExpandedProviders] = useState<Set<number>>(
    new Set(),
  );

  const reviewInvoicesData = useAppSelector(
    accountingSlice.selectNeedsReviewData,
  );
  const loading = useAppSelector(accountingSlice.selectLoadingNeedsReview);

  useEffect(() => {
    dispatch(
      accountingSlice.fetchNeedsReviewAsync({
        search: searchQuery || undefined,
        groupByProvider: groupBy === 'provider' ? true : undefined,
      }),
    );
  }, [dispatch, searchQuery, groupBy]);

  const onSearchChange = (val: string) => {
    setSearchQuery(val);
    setPage(1);
  };

  const toggleProvider = (providerId: number) => {
    setExpandedProviders((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(providerId)) {
        newSet.delete(providerId);
      } else {
        newSet.add(providerId);
      }
      return newSet;
    });
  };

  const handleReview = (invoiceId: string) => {
    if (!reviewInvoicesData) return;

    let allLineItems: ProviderInvoiceLineItem[] = [];
    if (reviewInvoicesData.groupedByProvider && reviewInvoicesData.providers) {
      reviewInvoicesData.providers.forEach((providerGroup) => {
        allLineItems.push(...providerGroup.lineItems);
      });
    } else if (reviewInvoicesData.lineItems) {
      allLineItems = reviewInvoicesData.lineItems;
    }

    const invoices = transformApiDataToInvoices(allLineItems);
    const invoice = invoices.find((inv) => inv.id === invoiceId);
    if (invoice) {
      setSelectedInvoice(invoice);
      setDialogOpen(true);
    }
  };

  const handleCloseDialog = (shouldRefresh?: boolean) => {
    setDialogOpen(false);
    setSelectedInvoice(null);
    if (shouldRefresh) {
      dispatch(
        accountingSlice.fetchNeedsReviewAsync({
          search: searchQuery || undefined,
          groupByProvider: groupBy === 'provider' ? true : undefined,
        }),
      );
    }
  };

  const renderPickup = (params: GridCellParams): React.ReactElement => {
    const row = params.row as InvoiceData;
    return (
      <CellDualValue primaryValue={row.pickup} secondaryValue={row.tripCode} />
    );
  };

  const renderProvider = (params: GridCellParams): React.ReactElement => {
    const row = params.row as InvoiceData;
    return (
      <Box className={classes.providerContainer}>
        <Box className={classes.providerContent}>
          <Box className={classes.providerText}>{row.provider}</Box>
          <Box className={classes.providerSubText}>{row.driverName}</Box>
        </Box>
        {row.driverBadge && <StyledTierChip label={row.driverBadge} />}
      </Box>
    );
  };

  const renderAddress = (params: GridCellParams): React.ReactElement => {
    const address = params.value as string;
    const { addressLine1, cityStateZip } = parseAddress(address);
    return (
      <Box className={classes.addressContainer}>
        <span>{addressLine1}</span>
        <span>{cityStateZip}</span>
      </Box>
    );
  };

  const renderMember = (params: GridCellParams): React.ReactElement => {
    const row = params.row as InvoiceData;
    return (
      <CellDualValue
        primaryValue={row.memberName}
        secondaryValue={row.memberId}
      />
    );
  };

  const renderInvoice = (params: GridCellParams): React.ReactElement => {
    const row = params.row as InvoiceData;
    const assignedToUser = row.rawData?.assignedToUser;

    return (
      <Box className={classes.invoiceContainer}>
        <Box className={classes.invoiceText}>{row.invoice}</Box>
        <CellActions>
          <ButtonException
            label="Review"
            disabled={assignedToUser ? true : false}
            onClick={(evt: React.MouseEvent<HTMLButtonElement>) => {
              evt.stopPropagation();
              if (!assignedToUser) {
                handleReview(row.id);
              }
            }}
          />
        </CellActions>
      </Box>
    );
  };

  const columns: GridColDef[] = [
    {
      field: 'pickup',
      headerName: t('invoice.pickup'),
      width: 200,
      sortable: true,
      renderCell: renderPickup,
    },
    {
      field: 'provider',
      headerName: t('invoice.provider'),
      width: 220,
      sortable: true,
      renderCell: renderProvider,
    },
    {
      field: 'puAddress',
      headerName: t('invoice.puAddress'),
      width: 250,
      sortable: false,
      renderCell: renderAddress,
    },
    {
      field: 'doAddress',
      headerName: t('invoice.doAddress'),
      width: 250,
      sortable: false,
      renderCell: renderAddress,
    },
    {
      field: 'member',
      headerName: t('invoice.member'),
      width: 200,
      sortable: true,
      renderCell: renderMember,
    },
    {
      field: 'invoice',
      headerName: t('invoice.invoice'),
      flex: 1,
      minWidth: 200,
      sortable: true,
      renderCell: renderInvoice,
    },
  ];

  const allInvoices = useMemo(() => {
    if (!reviewInvoicesData) return [];

    if (reviewInvoicesData.groupedByProvider && reviewInvoicesData.providers) {
      const allLineItems: ProviderInvoiceLineItem[] = [];
      reviewInvoicesData.providers.forEach((providerGroup) => {
        allLineItems.push(...providerGroup.lineItems);
      });
      return transformApiDataToInvoices(allLineItems);
    }

    if (!reviewInvoicesData.lineItems) return [];
    return transformApiDataToInvoices(reviewInvoicesData.lineItems);
  }, [reviewInvoicesData]);

  const paginatedInvoices = useMemo(() => {
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return allInvoices.slice(startIndex, endIndex);
  }, [allInvoices, page, pageSize]);

  const totalCount = allInvoices.length;

  const getRowClassName = (params: any) => {
    const row = params.row as InvoiceData;
    return row.rawData?.assignedToUser ? 'assigned-row' : '';
  };

  const renderGroupedView = () => {
    if (!reviewInvoicesData?.providers) return null;

    return (
      <Box className={classes.groupedGridContainer}>
        {reviewInvoicesData.providers.map((providerGroup) => {
          const providerId = providerGroup.provider.transportationProviderID;
          const isExpanded = expandedProviders.has(providerId);
          const invoices = transformApiDataToInvoices(providerGroup.lineItems);

          return (
            <Accordion
              key={providerId}
              expanded={isExpanded}
              onChange={() => toggleProvider(providerId)}
              className={classes.accordion}
              disableGutters>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                className={classes.accordionSummary}>
                <Box className={classes.providerHeader}>
                  <Typography className={classes.providerHeaderText}>
                    {providerGroup.provider.transportationProviderName}
                  </Typography>
                  <Box className={classes.providerCount}>
                    {providerGroup.lineItems.length}
                  </Box>
                </Box>
              </AccordionSummary>
              <AccordionDetails className={classes.accordionDetails}>
                <DataGridPro
                  loading={loading}
                  rows={invoices}
                  getRowId={(row) => row.id}
                  columns={columns as GridColDef[]}
                  sortingMode="server"
                  disableColumnSelector={true}
                  className={classes.grid}
                  disableRowSelectionOnClick
                  hideFooterSelectedRowCount
                  hideFooterRowCount
                  hideFooter
                  rowHeight={56}
                  disableColumnMenu={true}
                  disableColumnResize={true}
                  autoHeight
                  getRowClassName={getRowClassName}
                />
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Box>
    );
  };

  return (
    <StyledBox className={classes.container} maxWidth="xl">
      <Card className={classes.cardMain}>
        <CardHeader
          title={t('navigation.reviewInvoices')}
          className={classes.cardHeader}
        />
        <CardContent>
          <Box className={classes.toolbar}>
            <Box className={classes.toolContainer}>
              <TextSearch
                id="text-search"
                icon={<SearchIcon />}
                label={t('invoice.search')}
                currentSearchFilter={searchQuery}
                onSearchChange={onSearchChange}
              />
              <FormControl className={classes.toolInput}>
                <Select
                  value={groupBy}
                  onChange={(e) => setGroupBy(e.target.value)}
                  displayEmpty
                  renderValue={(value) => {
                    const labels: Record<string, string> = {
                      none: t('invoice.groupByNone'),
                      provider: t('invoice.groupByProvider'),
                    };

                    return (
                      <Box className={classes.groupByContainer}>
                        <Box className={classes.groupByLabel} component="span">
                          {t('invoice.groupBy')}
                        </Box>
                        <Box className={classes.groupByValue} component="span">
                          {labels[value] || value}
                        </Box>
                      </Box>
                    );
                  }}>
                  <MenuItem value="none">{t('invoice.groupByNone')}</MenuItem>
                  <MenuItem value="provider">
                    {t('invoice.groupByProvider')}
                  </MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>

          {groupBy === 'provider' &&
          reviewInvoicesData?.groupedByProvider &&
          reviewInvoicesData.providers ? (
            renderGroupedView()
          ) : (
            <>
              <DataGridPro
                loading={loading}
                rows={paginatedInvoices}
                getRowId={(row) => row.id}
                columns={columns as GridColDef[]}
                sortingMode="server"
                disableColumnSelector={true}
                className={`${classes.grid} ${paginatedInvoices.length === 0 ? 'empty' : ''}`}
                disableRowSelectionOnClick
                hideFooterSelectedRowCount
                hideFooterRowCount
                hideFooter
                rowHeight={56}
                disableColumnMenu={true}
                disableColumnResize={true}
                getRowClassName={getRowClassName}
              />
              <TablePagination
                component="div"
                count={totalCount}
                page={page - 1}
                onPageChange={(event, page) => setPage(page + 1)}
                rowsPerPage={pageSize}
                onRowsPerPageChange={(event) => {
                  setPageSize(Number.parseInt(event.target.value, 10));
                  setPage(1);
                }}
              />
            </>
          )}
        </CardContent>
      </Card>
      {selectedInvoice && (
        <ReviewInvoiceDialog
          open={dialogOpen}
          onClose={handleCloseDialog}
          providerInvoiceLineItemID={
            selectedInvoice.rawData?.providerInvoiceLineItemID || 0
          }
          providerInvoiceID={selectedInvoice.rawData?.providerInvoiceID || 0}
          invoiceData={{
            tripCode: selectedInvoice.tripCode || '',
            provider: selectedInvoice.provider || '',
            providerBadge: selectedInvoice.driverBadge,
            driverName: selectedInvoice.driverName || '',
            driverID: selectedInvoice.driverID,
            phoneNumber:
              selectedInvoice.rawData?.tripLeg.transportationProvider
                .accountNumber || '',
            reason:
              selectedInvoice.rawData?.providerInvoiceLineItemReviewLogs
                ?.length &&
              selectedInvoice.rawData?.providerInvoiceLineItemReviewLogs
                ?.length > 0
                ? selectedInvoice.rawData
                    ?.providerInvoiceLineItemReviewLogs?.[0].reviewReasonName ||
                  ''
                : '',
            tripId: selectedInvoice.tripCode || '',
            memberName: selectedInvoice.memberName || '',
            tripMode: selectedInvoice.rawData?.tripLeg.tripModeName || '',
            pickupTime: selectedInvoice.pickup || '',
            pickupDate: selectedInvoice.pickupDate || '',
            providerId:
              selectedInvoice.rawData?.tripLeg.transportationProviderID,
            appointmentDate: selectedInvoice.rawData?.tripLeg.appointmentDate,
            pickUpAddressTimeZoneIANA:
              selectedInvoice.rawData?.tripLeg.pickUpAddressTimeZoneIANA,
            pickupLocation: selectedInvoice.puAddress || '',
            dropoffLocation: selectedInvoice.doAddress || '',
            distance: selectedInvoice.rawData?.tripLeg.invoiceDistanceMiles
              ? `${selectedInvoice.rawData.tripLeg.invoiceDistanceMiles} miles`
              : '',
            price: selectedInvoice.rawData?.lineItemAmount
              ? `${selectedInvoice.rawData.lineItemAmount}`
              : '',
            onSite: selectedInvoice.rawData?.tripLeg.tripLegStatusLogs?.find(
              (log) => log.tripLegStatusBC === TripLegStatusBC.Onsite,
            )
              ? DateTime.fromISO(
                  selectedInvoice.rawData.tripLeg.tripLegStatusLogs.find(
                    (log) => log.tripLegStatusBC === TripLegStatusBC.Onsite,
                  )!.createdOnUTC,
                ).toFormat('h:mma')
              : undefined,
            onBoard: selectedInvoice.rawData?.tripLeg.tripLegStatusLogs?.find(
              (log) => log.tripLegStatusBC === TripLegStatusBC.Onboard,
            )
              ? DateTime.fromISO(
                  selectedInvoice.rawData.tripLeg.tripLegStatusLogs.find(
                    (log) => log.tripLegStatusBC === TripLegStatusBC.Onboard,
                  )!.createdOnUTC,
                ).toFormat('h:mma')
              : undefined,
            completed: selectedInvoice.rawData?.tripLeg.tripLegStatusLogs?.find(
              (log) => log.tripLegStatusBC === TripLegStatusBC.Completed,
            )
              ? DateTime.fromISO(
                  selectedInvoice.rawData.tripLeg.tripLegStatusLogs.find(
                    (log) => log.tripLegStatusBC === TripLegStatusBC.Completed,
                  )!.createdOnUTC,
                ).toFormat('h:mma')
              : undefined,
            reviewLogs:
              selectedInvoice.rawData?.providerInvoiceLineItemReviewLogs || [],
          }}
        />
      )}
    </StyledBox>
  );
}
